import React, { ReactNode, createContext, useContext, useState } from 'react';
import { cn } from '../../utils/cn';

interface TabsContextValue {
    activeTab: string;
    setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

const useTabsContext = () => {
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error('Tabs components must be used within a Tabs component');
    }
    return context;
};

export interface TabsProps {
    defaultValue?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    children: ReactNode;
    className?: string;
}

export const Tabs = ({
    defaultValue,
    value: controlledValue,
    onValueChange,
    children,
    className,
}: TabsProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue || '');

    const isControlled = controlledValue !== undefined;
    const activeTab = isControlled ? controlledValue : internalValue;

    const setActiveTab = (newValue: string) => {
        if (!isControlled) {
            setInternalValue(newValue);
        }
        onValueChange?.(newValue);
    };

    return (
        <TabsContext.Provider value={{ activeTab, setActiveTab }}>
            <div className={cn('w-full', className)}>{children}</div>
        </TabsContext.Provider>
    );
};

export interface TabsListProps {
    children: ReactNode;
    className?: string;
    variant?: 'default' | 'pills' | 'underline' | 'line';
}

export const TabsList = ({ children, className, variant = 'default' }: TabsListProps) => {
    const baseStyles = 'flex gap-1';

    const variantStyles = {
        default: 'bg-gray-100 dark:bg-gray-800 p-1 rounded-lg',
        pills: 'gap-2',
        underline: 'border-b border-gray-200 dark:border-gray-700',
        line: 'border-b border-gray-200 dark:border-gray-700',
    };

    return (
        <div className={cn(baseStyles, variantStyles[variant], className)} role="tablist">
            {children}
        </div>
    );
};

export interface TabsTriggerProps {
    value: string;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
}

export const TabsTrigger = ({ value, children, className, disabled = false }: TabsTriggerProps) => {
    const { activeTab, setActiveTab } = useTabsContext();
    const isActive = activeTab === value;

    return (
        <button
            role="tab"
            aria-selected={isActive}
            aria-disabled={disabled}
            disabled={disabled}
            onClick={() => !disabled && setActiveTab(value)}
            className={cn(
                'px-4 py-2.5 text-sm font-medium rounded-lg',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                isActive
                    ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50',
                className
            )}
        >
            {children}
        </button>
    );
};

export interface TabsContentProps {
    value: string;
    children: ReactNode;
    className?: string;
}

export const TabsContent = ({ value, children, className }: TabsContentProps) => {
    const { activeTab } = useTabsContext();

    if (activeTab !== value) {
        return null;
    }

    return (
        <div
            role="tabpanel"
            className={cn(
                'mt-4 focus:outline-none',
                'animate-fadeInUp',
                className
            )}
        >
            {children}
        </div>
    );
};

Tabs.displayName = 'Tabs';
TabsList.displayName = 'TabsList';
TabsTrigger.displayName = 'TabsTrigger';
TabsContent.displayName = 'TabsContent';

export default Tabs;
